import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

// Wrap children to fade & slide in when scrolled into view
export const Reveal = ({
  children,
  delay = 0,
  duration = 0.9,
  y = 40,
  x = 0,
  once = true,
  className = '',
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, x }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{
        duration,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Stagger container/items
export const StaggerGroup = ({ children, className = '', delay = 0.08 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'show' : 'hidden'}
      variants={{
        hidden: {},
        show: {
          transition: { staggerChildren: delay },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = '', y = 30 }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y },
      show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
      },
    }}
  >
    {children}
  </motion.div>
);

// Text mask reveal - characters slide up
export const TextReveal = ({ text, className = '', delay = 0, splitBy = 'word' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const items = splitBy === 'char' ? text.split('') : text.split(' ');

  return (
    <span ref={ref} className={className} style={{ display: 'inline-block' }}>
      {items.map((item, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            overflow: 'hidden',
            paddingBottom: '0.1em',
            paddingRight: splitBy === 'word' ? '0.25em' : '0',
          }}
        >
          <motion.span
            style={{ display: 'inline-block' }}
            initial={{ y: '110%' }}
            animate={inView ? { y: 0 } : {}}
            transition={{
              duration: 0.9,
              delay: delay + i * 0.05,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {item === ' ' ? '\u00A0' : item}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

export default Reveal;
